import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { fiscalYearLabel } from '@/lib/wareki';
import { Loading } from '@/components/Loading';
import { ErrorState } from '@/components/ErrorState';

/** GET /api/fiscal-years の要素 */
export interface FiscalYear {
  year: number;
  label: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
}

export const FISCAL_YEARS_KEY = ['fiscal-years'] as const;
const STORAGE_KEY = 'bs.selectedFiscalYear';

export function useFiscalYears() {
  return useQuery({
    queryKey: FISCAL_YEARS_KEY,
    queryFn: () => api.get<FiscalYear[]>('/fiscal-years'),
    staleTime: 10 * 60_000,
  });
}

interface FiscalYearContextValue {
  /** 選択中の事業年度（西暦） */
  year: number;
  fiscalYear: FiscalYear;
  years: FiscalYear[];
  /** 同じ画面のまま年度だけ切り替える */
  switchYear: (year: number) => void;
  /** `/years/:year` 付きのパスを作る。`yearPath('admin/users')` → `/years/2026/admin/users` */
  yearPath: (subPath?: string) => string;
}

const FiscalYearContext = createContext<FiscalYearContextValue | null>(null);

export function rememberYear(year: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(year));
  } catch {
    /* ignore */
  }
}

export function recallYear(): number | null {
  try {
    const v = Number(localStorage.getItem(STORAGE_KEY));
    return Number.isInteger(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

/** 既定の年度：前回選択 → 当年度 → 最新 */
export function pickDefaultYear(years: FiscalYear[]): number | null {
  const remembered = recallYear();
  if (remembered && years.some((y) => y.year === remembered)) return remembered;
  const current = years.find((y) => y.isCurrent);
  if (current) return current.year;
  const sorted = [...years].sort((a, b) => b.year - a.year);
  return sorted[0]?.year ?? null;
}

export function buildYearPath(year: number, subPath = ''): string {
  const s = subPath.replace(/^\/+/, '');
  return s ? `/years/${year}/${s}` : `/years/${year}`;
}

/**
 * `/years/:year` ルートの要素。URL の年度を検証し、配下へ年度コンテキストを提供する。
 */
export function FiscalYearProvider({ children }: { children: ReactNode }) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, isPending, error, refetch } = useFiscalYears();
  const year = Number(params.year);

  const years = useMemo(() => [...(data ?? [])].sort((a, b) => b.year - a.year), [data]);
  const fiscalYear = years.find((y) => y.year === year);

  const switchYear = useCallback(
    (next: number) => {
      rememberYear(next);
      const rest = location.pathname.replace(/^\/years\/[^/]+/, '');
      // 詳細・編集画面（ID を含む）は別年度に存在しないため、機能の一覧まで戻す
      const safeRest = rest.replace(/\/(\d+|[0-9a-f-]{36})(\/.*)?$/i, '');
      navigate(`/years/${next}${safeRest}`);
    },
    [location.pathname, navigate],
  );

  const yearPath = useCallback((sub?: string) => buildYearPath(year, sub), [year]);

  const value = useMemo<FiscalYearContextValue | null>(
    () => (fiscalYear ? { year, fiscalYear, years, switchYear, yearPath } : null),
    [fiscalYear, year, years, switchYear, yearPath],
  );

  if (isPending) return <Loading fullscreen label="事業年度を読み込んでいます" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  if (!value) {
    const fallback = pickDefaultYear(years);
    if (fallback == null) {
      return <ErrorState title="事業年度が登録されていません" error={null} description="管理者に事業年度の登録を依頼してください。" />;
    }
    const rest = location.pathname.replace(/^\/years\/[^/]+/, '');
    return <Navigate to={`/years/${fallback}${rest}`} replace />;
  }

  return <FiscalYearContext.Provider value={value}>{children}</FiscalYearContext.Provider>;
}

export function useFiscalYear(): FiscalYearContextValue {
  const ctx = useContext(FiscalYearContext);
  if (!ctx) throw new Error('useFiscalYear は /years/:year 配下で使用してください');
  return ctx;
}

/** API が label を返さない場合の表示ラベル */
export function displayYearLabel(fy: Pick<FiscalYear, 'year' | 'label'>): string {
  return fy.label || fiscalYearLabel(fy.year);
}
